/**
 * Student Course Viewer - Udemy-style LMS Interface
 * Production-ready course viewer with enrollment validation, progress tracking, device binding
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Chip,
  Button,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Article as ArticleIcon,
  VideoLibrary as VideoLibraryIcon,
  Link as LinkIcon,
  MenuBook as ReaderIcon,
  PictureAsPdf as PdfIcon,
  Style as FlashcardIcon,
  ArrowBack as ArrowBackIcon,
  Menu as MenuIcon,
  Terminal as TerminalIcon,
  GraphicEq as GraphicEqIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getApiBaseUrl } from '../../services/httpClient';
import { useCourseAccess } from '../../hooks/useCourseAccess';
import { useStudentProgress } from '../../hooks/useStudentProgress';
import FlashcardPlayer from './components/FlashcardPlayer';
import QuizPlayer from './components/QuizPlayer';
import ContentRenderer from './components/ContentRenderer';
import ContentFullscreenFrame from './components/ContentFullscreenFrame';
import AccessDeniedScreen from './components/AccessDeniedScreen';
import { useContentProtection } from '../../hooks/useContentProtection';
import CourseSubjectChapterSidebar from './components/CourseSubjectChapterSidebar';
import { groupChaptersBySubject } from '../../utils/courseSubjectGrouping';
import {
  findFallbackRunContentId,
} from './utils/practiceIdeUtils';
import { getStudentApiHeaders, getStudentApiParams } from '../../utils/studentPlatform';

const resolveContentDbId = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const text = String(value);
  const itemMatch = text.match(/^item-(\d+)$/i);
  if (itemMatch) return Number(itemMatch[1]);
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
};

const API_BASE_URL = getApiBaseUrl();

const getEffectiveContentType = (item) => {
  if (!item) {
    return 'text';
  }

  if (item.viewerType) {
    return String(item.viewerType).toLowerCase();
  }

  const rawType = String(item.type || '').toLowerCase();
  if (rawType === 'assignment' || item.assignment) {
    return 'assignment';
  }

  return rawType || 'text';
};

const mergeStudentContentData = (item) => {
  if (!item) return {};
  const nested = item.contentData || item.content_data || {};
  const pdfData = nested?.pdfData || nested?.pdf_data || {};
  return {
    ...nested,
    ...item,
    contentData: nested,
    pdfUrl:
      item.pdfUrl ||
      item.pdf_url ||
      nested.pdfUrl ||
      nested.pdf_url ||
      pdfData.pdfUrl ||
      pdfData.file_url ||
      '',
    imageUrl:
      item.imageUrl ||
      item.image_url ||
      nested.imageUrl ||
      nested.image_url ||
      pdfData.imageUrl ||
      pdfData.pdfUrl ||
      '',
    fileName:
      item.fileName ||
      item.file_name ||
      nested.fileName ||
      nested.file_name ||
      pdfData.fileName ||
      '',
  };
};

const getContentIcon = (type) => {
  switch (type) {
    case 'video': return <VideoLibraryIcon />;
    case 'link': return <LinkIcon />;
    case 'reader': return <ReaderIcon />;
    case 'pdf': return <PdfIcon />;
    case 'flashcards': return <FlashcardIcon />;
    case 'audio_source':
    case 'audio_loop': return <GraphicEqIcon />;
    default: return <ArticleIcon />;
  }
};

const getContentTypeLabel = (type) => {
  switch (type) {
    case 'reader':
      return 'reader notes';
    case 'link':
      return 'website link';
    case 'pdf':
      return 'legacy pdf';
    case 'audio_source':
      return 'full audio';
    case 'audio_loop':
      return 'verse practice';
    default:
      return type;
  }
};

const StudentCourseViewer = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [studentData, setStudentData] = useState(null);
  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedChapterId, setSelectedChapterId] = useState(null);
  const [selectedContentId, setSelectedContentId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  // Get student data from localStorage
  useEffect(() => {
    const studentDataStr = localStorage.getItem('student_data');
    if (!studentDataStr) {
      toast.error('Please login first');
      navigate('/student/login');
      return;
    }
    setStudentData(JSON.parse(studentDataStr));
  }, [navigate]);

  // Course access validation
  const { loading: accessLoading, hasAccess, accessStatus } = useCourseAccess(courseId, studentData);

  // Progress tracking
  const {
    progress,
    loading: progressLoading,
    markComplete,
    markAsAccessed,
    isCompleted
  } = useStudentProgress(studentData?.phone, courseId);

  // Load course data
  useEffect(() => {
    if (hasAccess && studentData) {
      loadCourseData();
    }
  }, [hasAccess, studentData]);

  const loadCourseData = async () => {
    setLoading(true);
    const builderUrl = `${API_BASE_URL}/lms/courses/${courseId}/builder`;
    const builderParams = getStudentApiParams({ publishedOnly: true });

    const applyBuilderResponse = (response) => {
      if (!response.data.success) return false;
      const publishedChapters = (response.data.chapters || []).filter(
        (ch) => ch.status === 'published',
      );
      const subjects = (response.data.subjects || []).filter(
        (subject) => subject.status !== 'deleted',
      );
      setCourseData({
        ...response.data,
        showPracticeIde:
          response.data.showPracticeIde === true
          || response.data.show_practice_ide === true,
        chapters: publishedChapters,
        subjects,
      });

      const subjectGroups = groupChaptersBySubject(publishedChapters, subjects);
      const firstChapter = subjectGroups[0]?.chapters?.[0];
      if (firstChapter) {
        setSelectedChapterId(firstChapter.id);
        const publishedContent =
          firstChapter.items?.filter((it) => it.status === 'published') || [];
        if (publishedContent.length > 0) {
          const firstContent = publishedContent[0];
          setSelectedContentId(firstContent.id);
          markAsAccessed(
            firstChapter.id,
            resolveContentDbId(firstContent.dbId ?? firstContent.id),
          );
        }
      }
      return true;
    };

    try {
      const response = await axios.get(builderUrl, {
        headers: getStudentApiHeaders(),
        params: builderParams,
      });

      if (!applyBuilderResponse(response)) {
        toast.error('Failed to load course content');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        try {
          localStorage.removeItem('student_token');
          const retryResponse = await axios.get(builderUrl, {
            headers: { 'X-Client-Platform': 'web' },
            params: builderParams,
          });
          if (applyBuilderResponse(retryResponse)) {
            return;
          }
        } catch (retryError) {
          console.error('Failed to load course after auth retry:', retryError);
        }
      }
      console.error('Failed to load course:', error);
      toast.error('Failed to load course content');
    } finally {
      setLoading(false);
    }
  };

  const handleContentSelect = (chapterId, contentId) => {
    setSelectedChapterId(chapterId);
    setSelectedContentId(contentId);
    const chapter = courseData?.chapters?.find((ch) => ch.id === chapterId);
    const item = chapter?.items?.find((it) => it.id === contentId);
    markAsAccessed(chapterId, resolveContentDbId(item?.dbId ?? contentId));
    if (isMobile) setSidebarOpen(false);

    if (item && getEffectiveContentType(item) === 'coding') {
      const resolvedId = item.dbId || item.id;
      navigate(`/student/courses/${courseId}/coding/${resolvedId}`, {
        state: {
          chapterTitle: chapter?.title,
          contentTitle: item.title,
        },
      });
    }
  };

  const handleMarkComplete = async (completionMeta = {}) => {
    if (!selectedChapterId || !selectedContentId || !courseData) return;
    const chapter = courseData.chapters.find((ch) => ch.id === selectedChapterId);
    const item = chapter?.items?.find((it) => it.id === selectedContentId);
    const contentDbId = resolveContentDbId(item?.dbId ?? selectedContentId);
    if (!contentDbId) return;

    const contentType = getEffectiveContentType(item);
    const metadata =
      contentType === 'flashcards'
        ? {
            cardsReviewed:
              completionMeta.cardsReviewed ?? item?.flashcards?.length ?? 0,
            ...completionMeta,
          }
        : completionMeta;

    if (contentType === 'quiz' && Array.isArray(completionMeta.answers)) {
      try {
        const token =
          localStorage.getItem('student_token') ||
          localStorage.getItem('token') ||
          '';
        await axios.post(
          `${getApiBaseUrl()}/lms/quizzes/${contentDbId}/submit`,
          {
            studentPhone: studentData?.phone,
            courseCode: courseId,
            chapterId: selectedChapterId,
            answers: completionMeta.answers,
            durationSeconds: completionMeta.durationSeconds || 0,
          },
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
      } catch (err) {
        if (err?.response?.status === 409) {
          toast.warning(
            err?.response?.data?.message ||
              'Already submitted. Ask faculty for another attempt.',
          );
          return;
        }
        console.warn('Quiz attempt archive failed', err);
      }
    }

    await markComplete(selectedChapterId, contentDbId, {
      ...metadata,
      score: completionMeta.score,
      totalMarks: completionMeta.totalMarks,
    });
    toast.success(
      contentType === 'quiz'
        ? `Quiz submitted — ${completionMeta.score ?? 0}/${completionMeta.totalMarks ?? 0}`
        : 'Marked as complete!',
    );
  };

  const checkContentCompleted = (chapterId, contentId) => {
    const chapter = courseData?.chapters?.find((ch) => ch.id === chapterId);
    const item = chapter?.items?.find(
      (it) => it.id === contentId || it.dbId === contentId,
    );
    const contentDbId = resolveContentDbId(item?.dbId ?? contentId);
    return contentDbId ? isCompleted(chapterId, contentDbId) : false;
  };

  const orderedChapters = useMemo(() => {
    if (!courseData) return [];
    return groupChaptersBySubject(
      courseData.chapters || [],
      courseData.subjects || [],
    ).flatMap((subject) => subject.chapters);
  }, [courseData]);

  const getNextContent = () => {
    if (!courseData || !selectedChapterId || !selectedContentId) return null;

    const currentChapter = orderedChapters.find((ch) => ch.id === selectedChapterId);
    if (!currentChapter) return null;

    const publishedItems =
      currentChapter.items?.filter((it) => it.status === 'published') || [];
    const currentIndex = publishedItems.findIndex((it) => it.id === selectedContentId);

    if (currentIndex < publishedItems.length - 1) {
      return {
        chapterId: currentChapter.id,
        contentId: publishedItems[currentIndex + 1].id,
        title: publishedItems[currentIndex + 1].title,
      };
    }

    const chapterIndex = orderedChapters.findIndex((ch) => ch.id === selectedChapterId);
    if (chapterIndex < orderedChapters.length - 1) {
      const nextChapter = orderedChapters[chapterIndex + 1];
      const nextChapterContent =
        nextChapter.items?.filter((it) => it.status === 'published') || [];
      if (nextChapterContent.length > 0) {
        return {
          chapterId: nextChapter.id,
          contentId: nextChapterContent[0].id,
          title: nextChapterContent[0].title,
        };
      }
    }

    return null;
  };

  const handleNext = () => {
    const next = getNextContent();
    if (next) {
      handleContentSelect(next.chapterId, next.contentId);
    } else {
      toast.success('🎉 Course completed!');
    }
  };

  // Calculate progress
  const calculateProgress = () => {
    if (!courseData) return 0;
    const totalItems = courseData.chapters.reduce((sum, ch) => {
      return sum + (ch.items?.filter(it => it.status === 'published').length || 0);
    }, 0);
    if (totalItems === 0) return 0;
    const completedCount = progress?.completedItems?.length || 0;
    return Math.round((completedCount / totalItems) * 100);
  };

  const selectedChapter = courseData?.chapters.find(ch => ch.id === selectedChapterId);
  const selectedContent = selectedChapter?.items?.find(it => it.id === selectedContentId);
  const selectedContentType = getEffectiveContentType(selectedContent);

  useContentProtection(Boolean(hasAccess && selectedContent));

  const protectionWatermark = [
    studentData?.name,
    studentData?.phone,
    courseData?.courseCode || courseId,
  ]
    .filter(Boolean)
    .join(' • ');

  // Loading states
  if (accessLoading || !studentData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Access denied
  if (!hasAccess) {
    return <AccessDeniedScreen accessStatus={accessStatus} courseId={courseId} />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const sidebarContent = (
    <CourseSubjectChapterSidebar
      chapters={courseData?.chapters || []}
      subjects={courseData?.subjects || []}
      progressPercent={calculateProgress()}
      selectedChapterId={selectedChapterId}
      selectedContentId={selectedContentId}
      isCompleted={checkContentCompleted}
      getContentIcon={getContentIcon}
      getContentTypeLabel={getContentTypeLabel}
      getEffectiveContentType={getEffectiveContentType}
      onContentSelect={handleContentSelect}
      isMobile={isMobile}
      onClose={() => setSidebarOpen(false)}
    />
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
      {/* Header */}
      <Paper
        elevation={1}
        sx={{
          mb: 0,
          borderRadius: 0,
          position: { xs: 'sticky', md: 'relative' },
          top: 0,
          zIndex: 1100,
        }}
      >
        <Container maxWidth="xl">
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ py: 1.5 }}
          >
            <IconButton onClick={() => navigate('/student/my-courses')} aria-label="Back to courses">
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {courseData?.courseName || courseData?.courseId || courseId}
              </Typography>
              {(courseData?.courseId || courseId) && (
                <Typography variant="caption" color="text.secondary">
                  {String(courseData?.courseId || courseId).toUpperCase()}
                </Typography>
              )}
            </Box>
            <Chip
              label={`${calculateProgress()}% Complete`}
              color="primary"
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
            />
          </Stack>
          {isMobile && (
            <Button
              fullWidth
              variant="contained"
              startIcon={<MenuIcon />}
              onClick={() => setSidebarOpen(true)}
              sx={{ mb: 1.5, textTransform: 'none', fontWeight: 700 }}
            >
              All chapters
            </Button>
          )}
        </Container>
      </Paper>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Grid container spacing={3}>
          {/* Sidebar - Desktop */}
          {!isMobile && (
            <Grid item xs={12} md={4} lg={3}>
              <Paper
                elevation={2}
                sx={{
                  height: { md: 'calc(100vh - 180px)' },
                  position: 'sticky',
                  top: 20,
                  overflow: 'hidden',
                  borderRadius: 2,
                }}
              >
                {sidebarContent}
              </Paper>
            </Grid>
          )}

          {/* Content Area */}
          <Grid item xs={12} md={8} lg={9}>
            {courseData?.showPracticeIde === true ? (
              <Paper
                elevation={2}
                onClick={() =>
                  navigate(`/student/courses/${courseId}/practice-ide`, {
                    state: {
                      fallbackRunContentId: findFallbackRunContentId(courseData?.chapters),
                    },
                  })
                }
                sx={{
                  p: 2,
                  mb: 2,
                  cursor: 'pointer',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'primary.light',
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: 'primary.light',
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <TerminalIcon />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                      Practice IDE
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Write and run code — no assignments or test cases
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Open →
                  </Typography>
                </Stack>
              </Paper>
            ) : null}
            <Paper elevation={2} sx={{ p: 3, minHeight: '70vh' }}>
              {selectedContent ? (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
                      {selectedChapter?.title && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {selectedChapter.title}
                        </Typography>
                      )}
                      <Typography variant="h5" fontWeight={700}>
                        {selectedContent.title}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      {checkContentCompleted(selectedChapterId, selectedContentId) ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Completed"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CheckCircleIcon />}
                          onClick={handleMarkComplete}
                        >
                          Mark Complete
                        </Button>
                      )}
                    </Stack>
                  </Stack>

                  <Divider sx={{ mb: 3 }} />

                  <ContentFullscreenFrame
                    title={selectedContent.title}
                    subtitle={selectedChapter?.title}
                    watermark={protectionWatermark}
                  >
                    {selectedContentType === 'flashcards' ? (
                      <FlashcardPlayer
                        flashcards={selectedContent.flashcards || []}
                        onComplete={handleMarkComplete}
                      />
                    ) : selectedContentType === 'quiz' ? (
                      <QuizPlayer
                        quiz={selectedContent.quiz || selectedContent.contentData || {}}
                        onComplete={handleMarkComplete}
                      />
                    ) : (
                      <ContentRenderer
                        content={
                          selectedContent.content ||
                          selectedContent.contentData?.content ||
                          selectedContent.contentData?.url ||
                          selectedContent.url ||
                          ''
                        }
                        type={selectedContentType}
                        contentId={selectedContent.dbId || selectedContent.id}
                        contentData={mergeStudentContentData(selectedContent)}
                        chapterId={selectedChapter?.dbId || selectedChapter?.id}
                        chapterTitle={selectedChapter?.title}
                        contentTitle={selectedContent.title}
                      />
                    )}
                  </ContentFullscreenFrame>

                  {/* Navigation */}
                  <Stack spacing={1.5} sx={{ mt: 4 }}>
                    {isMobile && (
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<MenuIcon />}
                        onClick={() => setSidebarOpen(true)}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        All chapters
                      </Button>
                    )}
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Button variant="outlined" onClick={() => navigate('/student/my-courses')}>
                        Back to Courses
                      </Button>
                      {getNextContent() && (
                        <Button variant="contained" onClick={handleNext}>
                          Next: {getNextContent().title}
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </>
              ) : (
                <Alert severity="info">
                  Select a lesson from the sidebar to begin
                </Alert>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Mobile Sidebar Drawer */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          PaperProps={{
            sx: {
              width: 'min(100vw, 360px)',
              maxWidth: '100%',
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      )}
    </Box>
  );
};

export default StudentCourseViewer;
